import React, { useCallback, useEffect, useMemo, useState } from "react";
import { createPortal } from "react-dom";
import {
  fetchJobCardActivityAudit,
  JobCardActivityItem,
  JobCardActivitySummary,
} from "../../services/jobCardActivityApi";
import {
  getModuleStyle,
  statusBadgeClass,
  isImageIcon,
  bootstrapIconClass,
} from "./jobCardActivityModuleConfig";
import { HistoryClockIcon, HistoryPersonIcon, SortTimeAscIcon, SortTimeDescIcon } from "./HistoryIcons";

const PAGE_SIZE = 100;
const TIMELINE_GUTTER_PX = 52;
const TIMELINE_LINE_LEFT = TIMELINE_GUTTER_PX / 2;

function activityTimeMs(item: JobCardActivityItem): number {
  const raw = item.occurred_at || "";
  if (!raw) return 0;
  const d = new Date(raw.includes("T") ? raw : raw.replace(" ", "T"));
  const t = d.getTime();
  return Number.isNaN(t) ? 0 : t;
}

export interface JobCardActivityDrawerProps {
  isOpen: boolean;
  onClose: () => void;
  jobcardId: string | null;
  rowSnapshot?: Record<string, any> | null;
  /** drawer = standalone portal; embedded = panel inside parent drawer */
  variant?: "drawer" | "embedded";
  /** For embedded: whether this tab is currently visible (loads independently) */
  isActive?: boolean;
}

function formatOccurredAt(iso: string): string {
  if (!iso) return "—";
  try {
    const d = new Date(iso.includes("T") ? iso : iso.replace(" ", "T"));
    if (Number.isNaN(d.getTime())) return iso;
    return d.toLocaleString(undefined, {
      hour: "2-digit",
      minute: "2-digit",
      day: "2-digit",
      month: "short",
      year: "numeric",
    });
  } catch {
    return iso;
  }
}

/** Split "Label: value · Label: value" (or ";") into readable rows for the timeline card. */
function descriptionRows(description: string): { label?: string; value: string }[] {
  const text = (description || "").trim();
  if (!text) return [];
  return text
    .split(/\s*[·;]\s*/)
    .map((part) => part.trim())
    .filter(Boolean)
    .map((part) => {
      const idx = part.indexOf(":");
      if (idx > 0 && idx < part.length - 1) {
        return {
          label: part.slice(0, idx).trim(),
          value: part.slice(idx + 1).trim(),
        };
      }
      return { value: part };
    });
}

function summaryFromRow(row: Record<string, any> | null | undefined): JobCardActivitySummary {
  return {
    jobcard_no: String(row?.jobcard_no ?? row?.jobcardNo ?? "—"),
    vin_no: String(row?.vin_no ?? row?.vin_number ?? "—"),
    vin_date: String(row?.vin_date ?? "—"),
    customer_name: String(row?.customername ?? row?.customer_name ?? "—"),
    vehicle_no: String(row?.platenumber ?? row?.plate_number ?? "—"),
    current_status: String(row?.issue_status ?? row?.current_state ?? row?.last_state ?? "—"),
    service_advisor: String(row?.created_by ?? "—"),
  };
}

function actorDisplayName(item: JobCardActivityItem): string {
  return item.actor_name || item.actor_username || item.actor_user_id || "—";
}

const ActivityModuleIcon: React.FC<{
  icon: string;
  fallbackIcon?: string;
}> = ({ icon, fallbackIcon }) => {
  const [useFallback, setUseFallback] = useState(false);
  const biClass = bootstrapIconClass(
    useFallback ? fallbackIcon || "bi-circle-fill" : isImageIcon(icon) ? fallbackIcon || "bi-circle-fill" : icon
  );

  if (isImageIcon(icon) && !useFallback) {
    return (
      <img
        src={icon}
        alt=""
        aria-hidden
        className="w-[18px] h-[18px] object-contain brightness-0 invert"
        onError={() => setUseFallback(true)}
      />
    );
  }

  return <i className={biClass} aria-hidden />;
};

const ActivityCard: React.FC<{ item: JobCardActivityItem }> = ({ item }) => {
  const mod = getModuleStyle(item.module);
  const matrix =
    item.additional_info && typeof item.additional_info === "object"
      ? Object.entries(item.additional_info)
      : [];

  return (
    <div className="relative flex gap-3 mb-5">
      <div
        className="relative z-10 flex shrink-0 justify-center pt-0.5"
        style={{ width: TIMELINE_GUTTER_PX }}
      >
        <div
          className={`w-[34px] h-[34px] rounded-full flex items-center justify-center text-white text-sm shadow-md ring-4 ring-slate-50 dark:ring-gray-900 ${mod.bgClass}`}
          style={{ backgroundColor: mod.color }}
        >
          <ActivityModuleIcon icon={mod.icon} fallbackIcon={mod.fallbackIcon} />
        </div>
      </div>
      <div className="flex-1 min-w-0 rounded-xl border border-white/70 bg-white/90 dark:bg-gray-800/90 dark:border-gray-700 p-4 shadow-sm hover:shadow-md transition-shadow">
        <div className="flex items-start justify-between gap-2 mb-1">
          <div>
            <div className="font-semibold text-sm text-slate-900 dark:text-gray-100">
              {item.activity_name}
            </div>
            <div className="text-xs text-slate-500 dark:text-gray-400 flex items-center gap-1 mt-0.5">
              <HistoryClockIcon className="w-3.5 h-3.5" />
              {formatOccurredAt(item.occurred_at)}
            </div>
          </div>
          <span
            className={`shrink-0 px-2.5 py-0.5 rounded-full text-[10px] font-semibold uppercase tracking-wide ${statusBadgeClass(item.status)}`}
          >
            {item.status}
          </span>
        </div>
        <div className="inline-flex items-center gap-1 text-xs font-medium text-slate-700 dark:text-gray-300 bg-slate-100 dark:bg-gray-700 px-2 py-0.5 rounded mt-1">
          <HistoryPersonIcon className="w-2.5 h-2.5" />
          {actorDisplayName(item)}
        </div>
        <span className="ml-2 text-[10px] font-semibold uppercase text-slate-500">
          • {item.module}
        </span>
        {item.description ? (
          <div className="mt-2 space-y-1">
            {descriptionRows(item.description).map((row, i) => (
              <div
                key={`${row.label || "line"}-${i}`}
                className="text-sm text-slate-600 dark:text-gray-400 leading-snug"
              >
                {row.label ? (
                  <>
                    <span className="font-medium text-slate-700 dark:text-gray-300">
                      {row.label}:
                    </span>{" "}
                    <span>{row.value}</span>
                  </>
                ) : (
                  <span>{row.value}</span>
                )}
              </div>
            ))}
          </div>
        ) : null}
        {matrix.length > 0 ? (
          <div className="mt-2 grid grid-cols-2 sm:grid-cols-3 gap-2 rounded-md bg-black/[0.03] dark:bg-white/5 p-2 text-[11px]">
            {matrix.map(([k, v]) => (
              <div key={k} className="flex flex-col">
                <span className="text-slate-500">{k}</span>
                <span className="font-semibold text-slate-800 dark:text-gray-200">{String(v)}</span>
              </div>
            ))}
          </div>
        ) : null}
        {item.remarks ? (
          <div className="mt-2 text-xs rounded-md border-l-[3px] border-primary-400 bg-primary-50/80 dark:bg-primary-900/20 px-3 py-2 text-slate-600 dark:text-gray-300">
            <span className="font-semibold">Remarks: </span>
            {item.remarks}
          </div>
        ) : null}
      </div>
    </div>
  );
};

export const JobCardActivityDrawer: React.FC<JobCardActivityDrawerProps> = ({
  isOpen,
  onClose,
  jobcardId,
  rowSnapshot,
  variant = "drawer",
  isActive = true,
}) => {
  const embedded = variant === "embedded";
  const [summary, setSummary] = useState<JobCardActivitySummary | null>(null);
  const [activities, setActivities] = useState<JobCardActivityItem[]>([]);
  const [totalCount, setTotalCount] = useState(0);
  const [skip, setSkip] = useState(0);
  const [loading, setLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState<string | null>(null);
  /** true = oldest first (matches API default); false = newest first */
  const [sortAsc, setSortAsc] = useState(true);
  const loadedJobcardRef = React.useRef<string | null>(null);

  const instantSummary = useMemo(() => summaryFromRow(rowSnapshot), [rowSnapshot]);

  const sortedActivities = useMemo(() => {
    const copy = [...activities];
    copy.sort((a, b) => {
      const diff = activityTimeMs(a) - activityTimeMs(b);
      if (diff !== 0) return sortAsc ? diff : -diff;
      // Stable tie-break by id so equal timestamps keep a consistent order
      const idA = String(a.id || "");
      const idB = String(b.id || "");
      return sortAsc ? idA.localeCompare(idB) : idB.localeCompare(idA);
    });
    return copy;
  }, [activities, sortAsc]);
  const load = useCallback(
    async (reset: boolean) => {
      if (!jobcardId) return;
      const nextSkip = reset ? 0 : skip;
      if (reset) {
        setLoading(true);
        setError(null);
      } else {
        setLoadingMore(true);
      }
      try {
        const res = await fetchJobCardActivityAudit(jobcardId, nextSkip, PAGE_SIZE);
        if (res?.success === false) {
          setError(res.message || "Could not load activity history.");
          if (reset) {
            setActivities([]);
            setSummary(instantSummary);
          }
          return;
        }
        const rows = Array.isArray(res.activities) ? res.activities : [];
        setSummary(res.summary ?? instantSummary);
        setTotalCount(typeof res.totalcount === "number" ? res.totalcount : rows.length);
        setActivities((prev) => (reset ? rows : [...prev, ...rows]));
        setSkip(reset ? rows.length : nextSkip + rows.length);
        loadedJobcardRef.current = jobcardId;
      } catch (e: any) {
        setError(e?.message || "Failed to load activity history.");
        if (reset) setSummary(instantSummary);
      } finally {
        setLoading(false);
        setLoadingMore(false);
      }
    },
    [jobcardId, skip, instantSummary]
  );

  useEffect(() => {
    if (!isOpen) {
      loadedJobcardRef.current = null;
      return;
    }
    if (embedded && !isActive) return;
    if (!jobcardId) {
      setActivities([]);
      setSummary(instantSummary);
      setError(null);
      return;
    }
    // Independent tabs: skip reload if this jobcard was already loaded while drawer open
    if (embedded && loadedJobcardRef.current === jobcardId) return;
    setActivities([]);
    setSkip(0);
    setSummary(instantSummary);
    setSortAsc(false);
    void load(true);
  }, [isOpen, isActive, jobcardId, embedded]);

  useEffect(() => {
    if (!isOpen || embedded) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    document.addEventListener("keydown", onKey);
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = "";
    };
  }, [isOpen, onClose, embedded]);

  if (!isOpen) return null;
  if (!embedded && !jobcardId) return null;

  const displaySummary = summary ?? instantSummary;
  const hasMore = activities.length < totalCount;
  const titleRef = displaySummary.jobcard_no !== "—"
    ? `Job Card Ref: ${displaySummary.jobcard_no}`
    : jobcardId
      ? `Job Card: ${jobcardId}`
      : "Job Card";

  const content = (
    <div className={`h-full overflow-y-auto p-5 custom-scrollbar ${embedded ? "" : ""}`}>
      {!jobcardId ? (
        <p className="text-sm text-slate-500 dark:text-gray-400 py-8 text-center">
          No job card linked to this inspection yet.
        </p>
      ) : (
        <>
          <div className="rounded-xl border border-black/5 bg-gradient-to-br from-white to-slate-100 dark:from-gray-800 dark:to-gray-900 p-4 text-sm leading-relaxed mb-5 shadow-sm">
            <p>
              <span className="font-semibold">Customer Name:</span> {displaySummary.customer_name}
            </p>
            <p>
              <span className="font-semibold">Vehicle:</span> {displaySummary.vehicle_no}
              {displaySummary.vin_no !== "—" ? ` (VIN: ${displaySummary.vin_no})` : ""}
            </p>
            <p>
              <span className="font-semibold">Service Advisor:</span>{" "}
              {displaySummary.service_advisor}
            </p>
            <p>
              <span className="font-semibold">Current Status:</span>{" "}
              {displaySummary.current_status}
            </p>
          </div>

          <div className="flex items-center justify-between gap-2 mb-3">
            <h3 className="text-sm font-semibold text-slate-800 dark:text-gray-200 m-0">
              Activity Progress ({sortAsc ? "Oldest → Newest" : "Newest → Oldest"})
            </h3>
            <button
              type="button"
              onClick={() => setSortAsc((v) => !v)}
              title={
                sortAsc
                  ? "Showing oldest first — click for newest first"
                  : "Showing newest first — click for oldest first"
              }
              aria-label={
                sortAsc
                  ? "Sort by date descending"
                  : "Sort by date ascending"
              }
              className="inline-flex items-center gap-1 shrink-0 px-2 py-1 rounded-md border border-slate-200 dark:border-gray-600 text-slate-600 dark:text-gray-300 hover:bg-slate-100 dark:hover:bg-gray-800 hover:text-primary-700 dark:hover:text-primary-300 transition-colors text-xs font-medium"
            >
              {sortAsc ? (
                <SortTimeAscIcon className="w-4 h-4" />
              ) : (
                <SortTimeDescIcon className="w-4 h-4" />
              )}
              <span>{sortAsc ? "Asc" : "Desc"}</span>
            </button>
          </div>

          {error ? (
            <div className="mb-4 rounded-lg border border-red-200 bg-red-50 dark:bg-red-900/20 px-3 py-2 text-sm text-red-800 dark:text-red-200 flex items-center justify-between gap-2">
              <span>{error}</span>
              <button
                type="button"
                className="text-xs font-semibold underline shrink-0"
                onClick={() => {
                  loadedJobcardRef.current = null;
                  void load(true);
                }}
              >
                Retry
              </button>
            </div>
          ) : null}

          {loading ? (
            <div className="flex items-center justify-center py-12 text-sm text-slate-500">
              <span className="inline-block h-6 w-6 border-2 border-primary-500 border-t-transparent rounded-full animate-spin mr-2" />
              Loading activity history…
            </div>
          ) : activities.length === 0 && !error ? (
            <p className="text-sm text-slate-500 dark:text-gray-400 py-8 text-center">
              No activity recorded yet for this job card.
            </p>
          ) : (
            <div className="relative">
              <div
                className="absolute top-1 bottom-1 w-[3px] rounded-full pointer-events-none"
                style={{
                  left: TIMELINE_LINE_LEFT,
                  transform: "translateX(-50%)",
                  background:
                    "linear-gradient(to bottom, var(--color-primary-600, #2ea04a), var(--color-primary-200, #bbf7d0))",
                }}
                aria-hidden
              />
              {sortedActivities.map((item) => (
                <ActivityCard key={item.id} item={item} />
              ))}
            </div>
          )}

          {hasMore && !loading ? (
            <div className="flex justify-center pt-2 pb-4">
              <button
                type="button"
                disabled={loadingMore}
                onClick={() => void load(false)}
                className="px-4 py-2 text-sm font-medium rounded-lg border border-primary-300 text-primary-700 dark:text-primary-300 hover:bg-primary-50 dark:hover:bg-primary-900/30 disabled:opacity-50"
              >
                {loadingMore ? "Loading…" : `Load more (${activities.length} of ${totalCount})`}
              </button>
            </div>
          ) : null}
        </>
      )}
    </div>
  );

  if (embedded) {
    return content;
  }

  const body = (
    <>
      <div
        className="fixed inset-0 z-[11999] bg-black/50 backdrop-blur-sm"
        aria-hidden
        onClick={onClose}
      />
      <div
        className="fixed top-0 right-0 z-[12000] h-full w-full max-w-[560px] bg-slate-50 dark:bg-gray-900 shadow-2xl flex flex-col border-l border-gray-200 dark:border-gray-700 transform transition-transform duration-300"
        role="dialog"
        aria-modal="true"
        aria-labelledby="job-card-activity-drawer-title"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="shrink-0 px-5 py-5 text-white bg-gradient-to-br from-primary-800 to-primary-500">
          <div className="flex items-start justify-between gap-3">
            <div>
              <h2 id="job-card-activity-drawer-title" className="text-base font-bold m-0">
                Activity Audit Log Flow
              </h2>
              <p className="text-xs opacity-90 mt-0.5">{titleRef}</p>
            </div>
            <button
              type="button"
              onClick={onClose}
              className="text-white/90 hover:text-white text-2xl leading-none px-1"
              aria-label="Close"
            >
              ×
            </button>
          </div>
        </div>

        <div className="flex-1 min-h-0 overflow-hidden">{content}</div>
      </div>
    </>
  );

  if (typeof document === "undefined") return null;
  return createPortal(body, document.body);
};

export default JobCardActivityDrawer;
